import React, { useEffect, useRef, useState } from "react";
import SignaturePad from "react-signature-canvas";
import axios from "axios";
import "../styles/Signature.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const SignatureManager = ({
  taskName, // legacy key (t_key)
  taskId = null, // preferred numeric ID
  userId,
  isAdmin,
  onSignComplete,
  referenceId = null, // e.g., employer ref for consent history
  strictReference = false,
  extraAnswers = {},
  autoReloadOnSign = true,
  onBeforeSign,
  refreshTrigger,
}) => {
  const sigPadRef = useRef(null);

  const [signatureURL, setSignatureURL] = useState(null);
  const [savedSignature, setSavedSignature] = useState(null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [isTaskSigned, setIsTaskSigned] = useState(false);

  // Load status + signatures for this user/task
  useEffect(() => {
    const fetchSignatures = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        const idBase = taskId != null ? String(taskId) : null;
        const idKey =
          idBase == null
            ? null
            : referenceId != null
              ? `${idBase}_${referenceId}`
              : idBase;
        const nameKey = taskName
          ? referenceId != null
            ? `${taskName}_${referenceId}`
            : taskName
          : null;
        const requiresReferenceRecord =
          strictReference && referenceId == null;

        const taskSig =
          (!requiresReferenceRecord && idKey && res.data.signatures?.[idKey]) ??
          (!requiresReferenceRecord &&
            nameKey &&
            res.data.signatures?.[nameKey]) ??
          null;

        const accountSignature = res.data.accountSignature || null;
        const latest = res.data.latestSignature || null;

        if (taskSig) {
          setSignatureURL(taskSig);
          setSavedSignature(null);
        } else if (accountSignature) {
          setSavedSignature(accountSignature);
          setSignatureURL(null);
        } else if (latest) {
          setSavedSignature(latest);
          setSignatureURL(null);
        } else {
          setSignatureURL(null);
          setSavedSignature(null);
        }

        const status =
          (!requiresReferenceRecord && idKey && res.data.status?.[idKey]) ??
          (!requiresReferenceRecord &&
            nameKey &&
            res.data.status?.[nameKey]) ??
          "not_completed";

        setIsTaskSigned(
          status === "review" ||
            status === "completed" ||
            status === "almost_expired"
        );
        setShowSignaturePad(false);
      } catch (err) {
        console.error("Error loading signature:", err);
      }
    };

    fetchSignatures();
  }, [userId, taskId, taskName, referenceId, strictReference, refreshTrigger]); // intentionally not including API_BASE

  const handleSubmitSignature = async (overrideSig = null) => {
    try {
      if (onBeforeSign) {
        await onBeforeSign();
      }
    } catch (err) {
      const message =
        err?.message || "Please complete the task before signing.";
      alert(message);
      return;
    }

    const signatureData =
      overrideSig || sigPadRef.current?.getCanvas().toDataURL("image/png");

    try {
      await axios.post(
        `${API_BASE}/api/usertasks/sign`,
        {
          taskName,
          taskId,
          userId,
          signature: signatureData,
          referenceId,
          ...extraAnswers,
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      );

      setSignatureURL(signatureData);
      setSavedSignature(null);
      setShowSignaturePad(false);

      if (autoReloadOnSign) {
        // verify then refresh
        try {
          const res = await axios.get(
            `${API_BASE}/api/usertasks/status/${userId}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            },
          );
          const idBase = taskId != null ? String(taskId) : null;
          const idKey =
            idBase == null
              ? null
              : referenceId != null
                ? `${idBase}_${referenceId}`
                : idBase;
          const nameKey = taskName
            ? referenceId != null
              ? `${taskName}_${referenceId}`
              : taskName
            : null;
          const completed =
            ((idKey && res.data.status?.[idKey]) ??
              (nameKey && res.data.status?.[nameKey]) ??
              "not_completed") === "completed";
          if (completed) window.location.reload();
          else window.location.reload();
        } catch {
          window.location.reload();
        }
      } else if (onSignComplete) {
        try {
          const res = await axios.get(
            `${API_BASE}/api/usertasks/status/${userId}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            },
          );
          const idBase = taskId != null ? String(taskId) : null;
          const idKey =
            idBase == null
              ? null
              : referenceId != null
                ? `${idBase}_${referenceId}`
                : idBase;
          const nameKey = taskName
            ? referenceId != null
              ? `${taskName}_${referenceId}`
              : taskName
            : null;
          const completed =
            ((idKey && res.data.status?.[idKey]) ??
              (nameKey && res.data.status?.[nameKey]) ??
              "not_completed") === "completed";
          onSignComplete(completed);
        } catch {
          onSignComplete(true);
        }
      }
    } catch (err) {
      console.error("Error saving signature:", err);
      alert("❌ Failed to save signature");
    }
  };

  const handleRedoSignature = async () => {
    try {
      await axios.post(
        `${API_BASE}/api/usertasks/reset`,
        { userId, taskName, taskId, referenceId },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      );

      // ✅ Immediately reset local UI state
      setIsTaskSigned(false);
      setSavedSignature(null);
      setSignatureURL(null);
      setShowSignaturePad(true);

      if (!autoReloadOnSign && onSignComplete) onSignComplete(false);

      // ✅ Auto-refresh the page so everything (status, dates, UI) syncs up
      window.location.reload();
    } catch (err) {
      console.error("❌ Reset signature failed:", err);
      alert("Failed to reset signature.");
    }
  };

  // --- Render ---
  if (isTaskSigned) {
    const imgSrc = signatureURL || savedSignature || null;
    return (
      <div className="mt-4">
        <label className="form-label">Signature:</label>
        <div className="signature-container">
          {imgSrc && (
            <img src={imgSrc} alt="Signature" className="signature-image" />
          )}
        </div>
        {!isAdmin && (
          <div className="alert alert-success text-center mt-3">
            ✔️ You have already signed this form.
          </div>
        )}
        {isAdmin && (
          <button
            className="btn btn-secondary mt-2"
            onClick={handleRedoSignature}
          >
            Re-do Signature 🔁
          </button>
        )}
      </div>
    );
  }

  if (savedSignature && !showSignaturePad) {
    return (
      <div className="mt-4">
        <label className="form-label">Signature:</label>
        <div className="signature-container">
        <img
          src={savedSignature}
          alt="Previous Signature"
          className="signature-image"
        />
        <div className="signature-buttons mt-2">
          <button
            className="btn btn-secondary me-2"
            onClick={() => setShowSignaturePad(true)}
          >
            Clear Signature 🧹
          </button>
          <button
            className="btn btn-success"
            onClick={() => handleSubmitSignature(savedSignature)}
          >
            Save Signature & Complete ✅
          </button>
        </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <label className="form-label">Signature:</label>
      <div className="signature-container">
        <SignaturePad
          ref={sigPadRef}
          canvasProps={{ className: "sigCanvas" }}
        />
        <div className="signature-buttons mt-2">
          <button
            className="btn btn-secondary me-2"
            onClick={() => sigPadRef.current?.clear()}
          >
            Clear Signature 🧹
          </button>
          <button
            className="btn btn-success"
            onClick={() => handleSubmitSignature()}
          >
            Save Signature & Complete ✅
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignatureManager;
